/*
  The results page's own layout — and with it, the mobile filter panel.

  The app block renders a results page with no panel around its facets: on a phone the filter list
  opened full-screen with no header, no close button and no footer, so the only way out was the
  browser's back button. Every facet group was expanded inline as well — 3,700px of brand
  checkboxes before the second group.

  The old theme answered this with the app's **`beforeInstantSearchMainTemplate` hook**, which
  replaces the whole InstantSearch layout, and that is what this does. It is the app's documented
  seam — nothing here patches app markup, and nothing has to race the app's own render: the app
  fills the containers this template declares exactly as it fills the ones in its default.

  An earlier attempt built the panel with DOM surgery instead, first from a MutationObserver (which
  deadlocked the renderer against the app's own re-renders) and then from the toggle's click (which
  never fired, because the app stops that click propagating). Both were solving a problem the hook
  does not have.

  The panel is an accordion (PHARM-1044), styled together with the collection page's in
  `filters.mobile-panel.css`: `.ais-dropdown-checkbox:checked ~ .ais-facet-subfacets` opens a group
  in place, so opening one costs no JavaScript at all. The checkbox comes before the label so the
  chevron can follow it too.
*/
window.PZSearch = window.PZSearch || {}

;(function (PZSearch) {
  'use strict'

  /* Inlined because it is part of a template string the hook returns, where `asset_url` cannot
     reach. Production's.

     The filter glyph is a line drawing, and it carried no stroke of its own: `ph-main.css` gives
     every `.icon` `fill: currentColor` and nothing a stroke, so the lines vanished and the two
     filled circles were left — "Show filters ˙." instead of the icon. Production's values
     (measured: no fill, the text colour, 7 units on the 64 grid, round caps) sit on the group, so
     the drawing does not depend on any stylesheet. */
  const FILTER_ICON =
    '<svg aria-hidden="true" focusable="false" class="icon icon-filter" viewBox="0 0 64 64">' +
    '<g fill="none" stroke="currentColor" stroke-width="7" stroke-linecap="round">' +
    '<path d="M48 42h10"/><path d="M48 42a5 5 0 1 1-5-5 5 5 0 0 1 5 5z"/><path d="M7 42h31"/>' +
    '<path d="M16 22H6"/><path d="M16 22a5 5 0 1 1 5 5 5 5 0 0 1-5-5z"/><path d="M57 22H26"/>' +
    '</g></svg>'

  /* Closing goes through the app's own toggle rather than the class it sets, so the app's idea of
     the state — and the button's own label — stay right. */
  function closePanel() {
    const button = document.querySelector('.ais-facets-button')
    if (button) button.click()
  }

  /*
    The footer's count mirrors the app's stats line. Called from the stats hook in the entry
    script, which fires on every InstantSearch render including the zero-hit one, so a refinement
    updates it without a timer — the old theme used a 500ms `setTimeout` after every click.
  */
  function syncFound() {
    const stats = document.querySelector('.ais-stats--nb-results')
    if (!stats) return
    const text = stats.textContent.trim()
    document.querySelectorAll('[data-ais-found]').forEach(function (node) {
      if (node.textContent !== text) node.textContent = text
    })
  }

  /*
    The selected-filter chips. The app renders each as a hidden group label ("Brand") followed by the
    text ": Sheglam", so with the label hidden — as production hides it — the chip read ": Sheglam".
    Production strips the colon after every render, from the `algolia.instantSearch.onRender` event;
    this runs from the stats hook instead (see afterRender), like the footer count — the same
    reasoning as search.algolia.js's render notification: a hook survives app updates that rename or
    drop events. Only the value's text node is touched, not the chip's markup as the old theme's
    `innerHTML` rewrite did.
  */
  function tidyRefinementChips() {
    document.querySelectorAll('.ais-current-refined-values--link > div').forEach(function (chip) {
      chip.childNodes.forEach(function (node) {
        if (node.nodeType === 3 && /^\s*:/.test(node.textContent)) {
          node.textContent = node.textContent.replace(/^\s*:\s*/, '')
        }
      })
    })
  }

  /*
    "Discount (3)" — how many values are ticked in each group, on the group's row. The app renders
    the values into the group's container, not the row this template owns, so the count is read off
    those after every render. Written into our own span only, and only when it changed.
  */
  function syncSelectedCounts() {
    document.querySelectorAll('.ais-facets .ais-facet-dropdown-wrapper').forEach(function (wrapper) {
      const slot = wrapper.querySelector('.ais-facet--selected-count')
      if (!slot) return
      const count = wrapper.querySelectorAll('.ais-RefinementList-item--selected').length
      const text = count ? '(' + count + ')' : ''
      if (slot.textContent !== text) slot.textContent = text
    })
  }

  // Run after each InstantSearch render, from the stats hook in search.algolia.js.
  function afterRender() {
    syncFound()
    tidyRefinementChips()
    syncSelectedCounts()
  }

  /*
    One delegated listener, in the capture phase. Capture because the app stops clicks inside the
    results page from propagating, so a bubble-phase listener on `document` is never called.
    Delegated because the app re-renders this subtree on every refinement, which would drop
    directly-bound handlers — the old theme re-bound them on a timeout.
  */
  function bind() {
    if (document.documentElement.dataset.pzAisPanelBound) return
    document.documentElement.dataset.pzAisPanelBound = '1'

    document.addEventListener(
      'click',
      function (event) {
        const target = event.target
        if (!target || typeof target.closest !== 'function') return

        /* The app's own click handler on the filters button overwrites it with a hardcoded English
           `textContent` ("Hide filters" / "Show filters"), dropping the label and the icon
           (PHARM-1054). This listener runs first (capture), so it saves the markup and puts it
           back from a one-off listener on the button itself: a listener added before the event
           reaches its target still fires there, after the app's. Production keeps one label in
           both states. */
        const facetsButton = target.closest('.ais-facets-button')
        if (facetsButton) {
          const markup = facetsButton.innerHTML
          facetsButton.addEventListener(
            'click',
            function () {
              facetsButton.innerHTML = markup
            },
            { once: true }
          )
          return
        }

        if (
          target.closest(
            '.ais-facets > .ais-facets-header .ais-facets-header__close, .ais-facet-mobile__apply-btn'
          )
        ) {
          closePanel()
          return
        }

        if (target.closest('.ais-facet-mobile__reset-btn')) {
          const clear = document.querySelector('.ais-ClearRefinements-button')
          if (clear) clear.click()
        }
      },
      true
    )
  }

  function createMainTemplate(config) {
    const t = (config && config.translations) || {}
    const strings = {
      searchTitle: t.searchTitle || '',
      showFilters: t.showFilters || 'Show filters',
      filters: t.filterTitle || 'Filter',
      clear: t.clear || 'Clear',
      apply: t.apply || 'Apply',
      close: t.close || 'Close',
      sortBy: t.sortBy || 'Sort',
      relevance: t.relevance || 'Relevance'
    }

    function template(_defaultTemplate, data, html) {
      const appConfig = (window.algoliaShopify && window.algoliaShopify.config) || {}
      const facets = (data && data.facets) || []

      /* Dynamic widgets render themselves into a single container rather than into a facet list a
         template can walk, so the loop is skipped when the app is running them. */
      const facetList = appConfig.app_blocks_dynamic_widgets_beta_enabled
        ? html`<div class="ais-dynamic-widgets-container"></div>`
        : facets.map(function (facet) {
            return html`
              <div
                class="ais-facet-dropdown-wrapper ais-facet-${facet.type} ais-facet-${facet.escapedName}"
              >
                <input
                  class="ais-dropdown-checkbox"
                  type="checkbox"
                  id="${facet.escapedName}"
                  name="dropdown"
                />
                <label for="${facet.escapedName}" class="ais-facet--header">
                  ${facet.title} <span class="ais-facet--selected-count"></span>
                </label>
                <div class="ais-facet-subfacets">
                  <div
                    class="ais-facet-${facet.escapedName}-container ais-facet-dropdown-container"
                  ></div>
                </div>
              </div>
            `
          })

      return html`
        <div class="ais-page">
          ${strings.searchTitle ? html`<h1 class="ais-h2">${strings.searchTitle}</h1>` : ''}
          <div class="ais-input">
            <div class="ais-search-box-container"></div>
            <div class="ais-input-button">
              <div class="ais-clear-input-icon"></div>
            </div>
          </div>
          <div class="ais-facets-actions">
            <div class="ais-facets-button">
              <span>${strings.showFilters}</span>
              ${html([FILTER_ICON])}
            </div>
            <div class="ais-clear-refinements-container"></div>
          </div>
          <div class="ais-current-refined-values-container"></div>
          <div class="ais-facets">
            <div class="ais-facets-header">
              <span class="ais-facets-header__title">${strings.filters}</span>
              <button
                type="button"
                class="ais-facets-header__close"
                aria-label="${strings.close}"
              ></button>
            </div>
            <div class="ais-facets-main-wrapper">${facetList}</div>
            <div class="ais-facet-mobile-footer">
              <div class="ais-facet-mobile__found"><span data-ais-found></span></div>
              <div class="ais-facet-mobile-footer__actions">
                <button type="button" class="btn ais-facet-mobile__reset-btn">
                  ${strings.clear}
                </button>
                <button type="button" class="btn ais-facet-mobile__apply-btn">
                  ${strings.apply}
                </button>
              </div>
            </div>
          </div>
          <div class="ais-block">
            <div class="ais-search-header">
              <div class="ais-stats-container"></div>
              <!-- The grid/list toggle is hidden in CSS, but the app looks it up while it mounts
                   and stops there if it is missing — taking the hits and the stats with it. -->
              <div class="ais-change-display">
                <span class="ais-change-display-block ais-change-display-selected"></span>
                <span class="ais-change-display-list"></span>
              </div>
              <div class="ais-sort">
                ${data && data.multipleSortOrders
                  ? html`${strings.sortBy}<span class="ais-sort-orders-container"></span>`
                  : html`${strings.sortBy} ${strings.relevance}`}
              </div>
            </div>
            <div class="ais-hits-container ais-results-as-block"></div>
          </div>
          <div class="ais-pagination-container"></div>
        </div>
      `
    }

    return { template: template, bind: bind, syncFound: syncFound, afterRender: afterRender }
  }

  PZSearch.createMainTemplate = createMainTemplate
})(window.PZSearch)
